import { getApiBaseUrl } from "src/config/endpoints";
import { normalizeApiLang, type ApiLang } from "src/utils/apiLang";
import { buildAuthHeaders, handleJsonResponse } from "./httpUtils";
import { appendSalesProductTenantId } from "./ShopifySalesProductAPI";

const API_BASE_URL = getApiBaseUrl();

const SALES_ATTRIBUTION_BASE = `${API_BASE_URL}/shopify/sales-product/sales-attribution`;

export type SalesAttributionPlatformFilter = "all" | "google_ads" | "meta_ads";

export interface SalesAttributionPeriod {
  start_date: string;
  end_date: string;
}

export interface SalesAttributionKpiTrio {
  value: number;
  formattedValue: string;
  previous_value: number;
  previous_valueFormattedValue: string;
  percent_change: number | null;
  percent_changeFormattedValue: string | null;
}

export interface SalesAttributionShopifyKpis {
  currency?: string | null;
  revenue: SalesAttributionKpiTrio;
  total_users: SalesAttributionKpiTrio;
}

export interface SalesAttributionGa4Kpis extends SalesAttributionShopifyKpis {
  available: boolean;
}

export interface SalesAttributionAdKpis {
  available: boolean;
  currency?: string | null;
  revenue: SalesAttributionKpiTrio;
  spend: SalesAttributionKpiTrio;
}

export interface SalesAttributionInvisibleChannelKpis {
  currency?: string | null;
  not_tracked_revenue: SalesAttributionKpiTrio;
  not_tracked_share_percent: SalesAttributionKpiTrio;
  not_tracked_orders: SalesAttributionKpiTrio;
}

export interface SalesAttributionKpisResponse {
  period: SalesAttributionPeriod;
  comparison_period: SalesAttributionPeriod;
  shopify: SalesAttributionShopifyKpis;
  ga4: SalesAttributionGa4Kpis;
  google_ads: SalesAttributionAdKpis;
  meta_ads: SalesAttributionAdKpis;
  invisible_channel: SalesAttributionInvisibleChannelKpis;
  warnings?: string[];
}

export interface SalesAttributionAvailability {
  ga4: boolean;
  google_ads: boolean;
  meta_ads: boolean;
}

export interface SalesAttributionCurrencies {
  shopify?: string | null;
  ga4?: string | null;
  google_ads?: string | null;
  meta_ads?: string | null;
}
export interface SalesAttributionReconciliationRow {
  date: string;
  dateFormattedValue: string;

  shopify_orders: number;
  shopify_ordersFormattedValue: string;
  ga4_orders: number;
  ga4_ordersFormattedValue: string;
  google_ads_orders_shopify: number;
  google_ads_orders_shopifyFormattedValue: string;
  meta_ads_orders_shopify: number;
  meta_ads_orders_shopifyFormattedValue: string;
  google_ads_orders_ga4: number;
  google_ads_orders_ga4FormattedValue: string;
  meta_ads_orders_ga4: number;
  meta_ads_orders_ga4FormattedValue: string;
  google_ads_orders: number;
  google_ads_ordersFormattedValue: string;
  meta_ads_orders: number;
  meta_ads_ordersFormattedValue: string;

  shopify_revenue: number;
  shopify_revenueFormattedValue: string;
  ga4_revenue: number;
  ga4_revenueFormattedValue: string;
  google_ads_revenue: number;
  google_ads_revenueFormattedValue: string;
  meta_ads_revenue: number;
  meta_ads_revenueFormattedValue: string;

  google_ads_spend: number;
  google_ads_spendFormattedValue: string;
  meta_ads_spend: number;
  meta_ads_spendFormattedValue: string;
}

export interface SalesAttributionAppliedFilters {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  platform: SalesAttributionPlatformFilter | null;
  campaign: string | null;
}

export interface SalesAttributionReconciliationResponse {
  period: SalesAttributionPeriod;
  filters: SalesAttributionAppliedFilters;
  currencies: SalesAttributionCurrencies;
  availability: SalesAttributionAvailability;
  rows: SalesAttributionReconciliationRow[];
  notes?: string[];
}

export interface SalesAttributionChannelComparisonRow {
  channel: string;
  label: string;
  shopify_revenue: number;
  shopify_revenueFormattedValue: string;
  ga4_revenue: number;
  ga4_revenueFormattedValue: string;
}

export interface SalesAttributionShopifyVsGa4Response {
  period: SalesAttributionPeriod;
  filters: Pick<
    SalesAttributionAppliedFilters,
    "utm_source" | "utm_medium" | "utm_campaign"
  >;
  currencies: Pick<SalesAttributionCurrencies, "shopify" | "ga4">;
  rows: SalesAttributionChannelComparisonRow[];
  warnings?: string[];
  notes?: string[];
}

export type SalesAttributionRevenueSource = "shopify" | "ga4";

export interface SalesAttributionScorecardRow {
  channel: string;
  label: string;
  currency?: string | null;
  spend: number;
  spendFormattedValue: string;
  platform_revenue: number;
  platform_revenueFormattedValue: string;
  attributed_revenue: number;
  attributed_revenueFormattedValue: string;
  roas: number;
  roasFormattedValue: string;
  orders: number;
  ordersFormattedValue: string;
  impressions: number;
  impressionsFormattedValue: string;
  clicks: number;
  clicksFormattedValue: string;
  ctr: number;
  ctrFormattedValue: string;
}

export interface SalesAttributionScorecardResponse {
  period: SalesAttributionPeriod;
  attributed_revenue_source?: SalesAttributionRevenueSource;
  rows: SalesAttributionScorecardRow[];
  total: SalesAttributionScorecardRow | null;
  warnings?: string[];
  notes?: string[];
}

export interface SalesAttributionOverlapSet {
  google_ads_claims: number;
  google_ads_claimsFormattedValue: string;
  meta_ads_claims: number;
  meta_ads_claimsFormattedValue: string;
  google_only: number;
  google_onlyFormattedValue: string;
  meta_only: number;
  meta_onlyFormattedValue: string;
  claimed_by_both: number;
  claimed_by_bothFormattedValue: string;
  overlap_percent: number;
  overlap_percentFormattedValue: string;
}

export interface SalesAttributionOverlapResponse {
  period: SalesAttributionPeriod;
  currency?: string | null;
  orders: SalesAttributionOverlapSet;
  revenue: SalesAttributionOverlapSet;
  notes?: string[];
}

export interface SalesAttributionFilterOption {
  value: string;
  label: string;
}

export interface SalesAttributionPlatformOption extends SalesAttributionFilterOption {
  available?: boolean;
}

export interface SalesAttributionCampaignOption extends SalesAttributionFilterOption {
  platforms: string[];
}

export interface SalesAttributionFiltersResponse {
  period: SalesAttributionPeriod;
  utm_sources: SalesAttributionFilterOption[];
  utm_mediums: SalesAttributionFilterOption[];
  utm_campaigns: SalesAttributionFilterOption[];
  platforms: SalesAttributionPlatformOption[];
  campaigns: SalesAttributionCampaignOption[];
  warnings?: string[];
  notes?: string[];
}

export interface SalesAttributionRequestOptions {
  tenantId?: number;
  lang?: ApiLang;
}

/** The five filter-bar selections, each optional. */
export interface SalesAttributionFilterParams {
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  platform?: SalesAttributionPlatformFilter | null;
  campaign?: string | null;
}

const buildParams = (
  startDate: string,
  endDate: string,
  options?: SalesAttributionRequestOptions,
  filters?: SalesAttributionFilterParams,
): URLSearchParams => {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
    lang: normalizeApiLang(options?.lang),
  });
  appendSalesProductTenantId(params, options?.tenantId);
  for (const [key, value] of Object.entries(filters ?? {})) {
    if (value !== undefined && value !== null && value !== "") {
      params.set(key, String(value));
    }
  }
  return params;
};

const getSalesAttribution = async <TResponse,>(
  path: string,
  params: URLSearchParams,
): Promise<TResponse> => {
  const response = await fetch(`${SALES_ATTRIBUTION_BASE}/${path}?${params.toString()}`, {
    method: "GET",
    headers: buildAuthHeaders("api_key"),
  });
  return handleJsonResponse<TResponse>(response);
};

export const salesAttributionService = {
  fetchKpis: (
    startDate: string,
    endDate: string,
    options?: SalesAttributionRequestOptions,
  ) =>
    getSalesAttribution<SalesAttributionKpisResponse>(
      "kpis",
      buildParams(startDate, endDate, options),
    ),

  /** All five filters apply. One row per day, unpaginated. */
  fetchReconciliation: (
    startDate: string,
    endDate: string,
    filters?: SalesAttributionFilterParams,
    options?: SalesAttributionRequestOptions,
  ) =>
    getSalesAttribution<SalesAttributionReconciliationResponse>(
      "reconciliation",
      buildParams(startDate, endDate, options, filters),
    ),

  /** Only the three `utm_*` filters apply here. */
  fetchShopifyVsGa4ByChannel: (
    startDate: string,
    endDate: string,
    filters?: Pick<
      SalesAttributionFilterParams,
      "utm_source" | "utm_medium" | "utm_campaign"
    >,
    options?: SalesAttributionRequestOptions,
  ) =>
    getSalesAttribution<SalesAttributionShopifyVsGa4Response>(
      "shopify-vs-ga4-by-channel",
      buildParams(startDate, endDate, options, filters),
    ),

  fetchChannelScorecard: (
    startDate: string,
    endDate: string,
    revenueSource?: SalesAttributionRevenueSource,
    options?: SalesAttributionRequestOptions,
  ) => {
    const params = buildParams(startDate, endDate, options);
    if (revenueSource) {
      params.set("attributed_revenue_source", revenueSource);
    }
    return getSalesAttribution<SalesAttributionScorecardResponse>(
      "channel-scorecard",
      params,
    );
  },

  fetchPlatformOverlap: (
    startDate: string,
    endDate: string,
    options?: SalesAttributionRequestOptions,
  ) =>
    getSalesAttribution<SalesAttributionOverlapResponse>(
      "platform-overlap",
      buildParams(startDate, endDate, options),
    ),

  fetchFilters: (
    startDate: string,
    endDate: string,
    options?: SalesAttributionRequestOptions,
  ) =>
    getSalesAttribution<SalesAttributionFiltersResponse>(
      "filters",
      buildParams(startDate, endDate, options),
    ),
};
